import React, { useEffect, useState } from "react";
import {
  Radio, Eye, Users, Inbox, Briefcase, FileText, LineChart, Plus,
  LayoutTemplate, Image, ArrowRight, Mail,
} from "lucide-react";
import { api } from "@/lib/api";

function fmtDur(s) { if (!s) return "0s"; const m = Math.floor(s / 60); return m ? `${m}m` : `${s}s`; }

function Kpi({ icon: Icon, label, value, accent, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={!onClick}
      className={`text-left border border-primary-gold/15 p-6 bg-charcoal transition-colors ${onClick ? "hover:border-primary-gold/50 cursor-pointer" : "cursor-default"}`}
      data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-text mb-3">
        <Icon size={15} strokeWidth={1.5} className={accent ? "text-luxury-gold" : ""} />
        <span className="text-xs tracking-wide uppercase">{label}</span>
      </div>
      <div className={`serif-italic text-4xl ${accent ? "text-luxury-gold" : "text-ivory"}`}>{value}</div>
    </button>
  );
}

export default function Overview({ data, user, setTab }) {
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/summary", { params: { days: 7 } }).then(({ data }) => setA(data)).catch(() => {});
  }, []);

  const recent = [
    ...data.contacts.filter((c) => !c.archived).map((c) => ({ ...c, _type: "contact", _title: `${c.first_name} ${c.last_name}`, _sub: c.reason || c.email })),
    ...data.opportunities.filter((o) => !o.archived).map((o) => ({ ...o, _type: "opportunity", _title: o.company, _sub: o.founder_name || o.sector })),
  ].sort((x, y) => new Date(y.created_at) - new Date(x.created_at)).slice(0, 6);

  const contactCount = data.contacts.filter((c) => !c.archived).length;
  const oppCount = data.opportunities.filter((o) => !o.archived).length;

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const name = (user?.name || user?.email || "").split("@")[0].split(" ")[0];

  const actions = [
    { label: "Add Portfolio", icon: Plus, tab: "portfolio" },
    { label: "Add Insight", icon: FileText, tab: "insights" },
    { label: "Edit Page Content", icon: LayoutTemplate, tab: "pages" },
    { label: "Media Library", icon: Image, tab: "media" },
    { label: "View Analytics", icon: LineChart, tab: "analytics" },
  ];

  return (
    <div data-testid="admin-overview" className="space-y-10">
      <div>
        <p className="eyebrow mb-2">Dashboard</p>
        <h1 className="text-3xl md:text-4xl font-light text-ivory">{greet}{name ? `, ${name}` : ""}.</h1>
        <p className="text-sm text-muted-text mt-2">{new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
      </div>

      <div>
        <p className="eyebrow mb-4">Last 7 days</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Radio} label="Active Now" value={a?.active_now ?? "—"} accent onClick={() => setTab("analytics")} testid="ov-active" />
          <Kpi icon={Eye} label="Pageviews" value={a?.total_views ?? "—"} onClick={() => setTab("analytics")} testid="ov-views" />
          <Kpi icon={Users} label="Unique Visitors" value={a?.unique_visitors ?? "—"} onClick={() => setTab("analytics")} testid="ov-visitors" />
          <Kpi icon={LineChart} label="Avg. Session" value={a ? fmtDur(a.avg_session_seconds) : "—"} onClick={() => setTab("analytics")} testid="ov-session" />
        </div>
      </div>

      <div>
        <p className="eyebrow mb-4">Content & enquiries</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Inbox} label="Contact Messages" value={contactCount} onClick={() => setTab("submissions")} testid="ov-contacts" />
          <Kpi icon={Inbox} label="Opportunities" value={oppCount} onClick={() => setTab("submissions")} testid="ov-opportunities" />
          <Kpi icon={Briefcase} label="Portfolio Items" value={data.portfolio.length} onClick={() => setTab("portfolio")} testid="ov-portfolio" />
          <Kpi icon={Mail} label="Subscribers" value={data.newsletter.length} onClick={() => setTab("newsletter")} testid="ov-subscribers" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/5 bg-charcoal p-6" data-testid="ov-recent">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-xs tracking-wide uppercase text-primary-gold">Recent Submissions</h3>
            <button onClick={() => setTab("submissions")} className="text-xs text-muted-text hover:text-primary-gold flex items-center gap-1" data-testid="ov-view-all">View all <ArrowRight size={13} /></button>
          </div>
          <div className="space-y-2">
            {recent.length === 0 && <p className="text-muted-text text-sm">No submissions yet.</p>}
            {recent.map((r) => (
              <button key={`${r._type}-${r.id}`} onClick={() => setTab("submissions")} className="w-full text-left flex items-center justify-between gap-4 py-3 border-b border-white/5 hover:bg-obsidian/50 transition-colors px-2" data-testid="ov-recent-row">
                <div className="min-w-0">
                  <span className="text-sm text-ivory">{r._title}</span>
                  <span className="text-xs text-muted-text ml-2">· {r._sub}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${r._type === "opportunity" ? "border-luxury-gold/40 text-luxury-gold" : "border-white/15 text-muted-text"}`}>{r._type}</span>
                  <span className="text-xs text-muted-text whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="border border-white/5 bg-charcoal p-6" data-testid="ov-actions">
          <h3 className="text-xs tracking-wide uppercase text-primary-gold mb-5">Quick Actions</h3>
          <div className="space-y-2">
            {actions.map((ac) => (
              <button key={ac.tab} onClick={() => setTab(ac.tab)} className="w-full flex items-center gap-3 px-4 py-3 border border-white/10 text-soft-stone hover:border-primary-gold/50 hover:text-primary-gold transition-colors text-sm" data-testid={`ov-action-${ac.tab}`}>
                <ac.icon size={15} strokeWidth={1.5} /> {ac.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {a?.top_pages?.length > 0 && (
        <div className="border border-white/5 bg-charcoal p-6" data-testid="ov-top-pages">
          <h3 className="text-xs tracking-wide uppercase text-primary-gold mb-5">Top Pages (7 days)</h3>
          <div className="space-y-3">
            {a.top_pages.slice(0, 5).map((p) => {
              const max = a.top_pages[0].count || 1;
              return (
                <div key={p.name}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-soft-stone truncate mr-3">{p.name}</span><span className="text-muted-text">{p.count}</span></div>
                  <div className="h-1.5 bg-obsidian"><div className="h-full bg-primary-gold/70" style={{ width: `${(p.count / max) * 100}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
